/**
 * Conversion between the visual field editor's ReviewFieldDefinition[] and the
 * JSON-Schema shape stored on disk (review_configs.response_fields_schema, and
 * an `agent` workflow node's inline outputSchema).
 *
 * Extracted verbatim from ReviewConfigForm so the workflow agent node can reuse
 * the same editor and produce byte-identical schemas. `convertFieldsToSchema`
 * previously closed over the form's `schemaFields` / `requiredFields` state;
 * they are parameters now, and nothing else about the behaviour changed.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import type { ReviewFieldDefinition } from './ReviewFieldEditor';

export const convertSchemaToFields = (schema: any): ReviewFieldDefinition[] => {
  if (!schema?.properties) return [];

  const fields: ReviewFieldDefinition[] = [];
  const properties = schema.properties;

  const schemaRequired = schema.required || [];

  for (const [name, prop] of Object.entries(properties)) {
    const propDef = prop as any;
    const field: ReviewFieldDefinition = {
      name,
      type: propDef.type,
      description: propDef.description || '',
      required: schemaRequired.includes(name),
      nullable: propDef.nullable,
      enum: propDef.enum,
    };

    if (propDef.type === 'array' && propDef.items) {
      field.items = {
        type: propDef.items.type,
        enum: propDef.items.enum,
      };

      if (propDef.items.properties) {
        field.items.properties = [];
        const itemRequired = propDef.items.required || [];
        for (const [nestedName, nestedProp] of Object.entries(propDef.items.properties)) {
          const nestedField = convertPropertyToField(nestedName, nestedProp);
          nestedField.required = itemRequired.includes(nestedName);
          field.items.properties.push(nestedField);
        }
        field.items.required = propDef.items.required;
      }
    }

    if (propDef.type === 'object' && propDef.properties) {
      field.properties = [];
      const nestedRequired = propDef.required || [];
      for (const [nestedName, nestedProp] of Object.entries(propDef.properties)) {
        const nestedField = convertPropertyToField(nestedName, nestedProp);
        nestedField.required = nestedRequired.includes(nestedName);
        field.properties.push(nestedField);
      }
      field.requiredFields = propDef.required;
    }

    fields.push(field);
  }

  return fields;
};

const convertPropertyToField = (name: string, prop: any): ReviewFieldDefinition => {
  const field: ReviewFieldDefinition = {
    name,
    type: prop.type,
    description: prop.description || '',
    nullable: prop.nullable,
    enum: prop.enum,
  };

  if (prop.type === 'array' && prop.items) {
    field.items = {
      type: prop.items.type,
      enum: prop.items.enum,
    };

    if (prop.items.properties) {
      field.items.properties = [];
      const itemRequired = prop.items.required || [];
      for (const [nestedName, nestedProp] of Object.entries(prop.items.properties)) {
        const nestedField = convertPropertyToField(nestedName, nestedProp);
        nestedField.required = itemRequired.includes(nestedName);
        field.items.properties.push(nestedField);
      }
      field.items.required = prop.items.required;
    }
  }

  if (prop.type === 'object' && prop.properties) {
    field.properties = [];
    const nestedRequired = prop.required || [];
    for (const [nestedName, nestedProp] of Object.entries(prop.properties)) {
      const nestedField = convertPropertyToField(nestedName, nestedProp);
      nestedField.required = nestedRequired.includes(nestedName);
      field.properties.push(nestedField);
    }
    field.requiredFields = prop.required;
  }

  return field;
};

export const convertFieldsToSchema = (
  schemaFields: ReviewFieldDefinition[],
  requiredFields: string[],
): any => {
  if (schemaFields.length === 0) {
    return undefined; // Return undefined if no fields - will use hardcoded default schema
  }

  const properties: Record<string, any> = {};

  schemaFields.forEach((field) => {
    if (!field.name) return;

    const propDef: Record<string, any> = {
      type: field.type,
      description: field.description || '',
    };

    if (field.nullable) {
      propDef.nullable = true;
    }

    if (field.enum && field.enum.length > 0) {
      propDef.enum = field.enum;
    }

    if (field.type === 'array' && field.items) {
      propDef.items = {
        type: field.items.type,
      };

      if (field.items.properties && field.items.properties.length > 0) {
        propDef.items.properties = {};
        field.items.properties.forEach((nestedProp: ReviewFieldDefinition) => {
          if (nestedProp.name) {
            propDef.items.properties[nestedProp.name] = convertFieldDefToProperty(nestedProp);
          }
        });
        if (field.items.required && field.items.required.length > 0) {
          propDef.items.required = field.items.required;
        }
      }

      if (field.items.enum && field.items.enum.length > 0) {
        propDef.items.enum = field.items.enum;
      }
    }

    if (field.type === 'object' && field.properties && field.properties.length > 0) {
      propDef.properties = {};
      field.properties.forEach((nestedProp: ReviewFieldDefinition) => {
        if (nestedProp.name) {
          propDef.properties[nestedProp.name] = convertFieldDefToProperty(nestedProp);
        }
      });
      if (field.requiredFields && field.requiredFields.length > 0) {
        propDef.required = field.requiredFields;
      }
    }

    properties[field.name] = propDef;
  });

  return {
    type: 'object',
    properties,
    required: requiredFields,
  };
};

const convertFieldDefToProperty = (field: ReviewFieldDefinition): Record<string, any> => {
  const prop: Record<string, any> = {
    type: field.type,
    description: field.description || '',
  };

  if (field.nullable) {
    prop.nullable = true;
  }

  if (field.enum && field.enum.length > 0) {
    prop.enum = field.enum;
  }

  if (field.type === 'array' && field.items) {
    prop.items = {
      type: field.items.type,
    };

    if (field.items.properties && field.items.properties.length > 0) {
      prop.items.properties = {};
      field.items.properties.forEach((nestedProp: ReviewFieldDefinition) => {
        if (nestedProp.name) {
          prop.items.properties[nestedProp.name] = convertFieldDefToProperty(nestedProp);
        }
      });
      if (field.items.required && field.items.required.length > 0) {
        prop.items.required = field.items.required;
      }
    }
  }

  if (field.type === 'object' && field.properties && field.properties.length > 0) {
    prop.properties = {};
    field.properties.forEach((nestedProp: ReviewFieldDefinition) => {
      if (nestedProp.name) {
        prop.properties[nestedProp.name] = convertFieldDefToProperty(nestedProp);
      }
    });
    if (field.requiredFields && field.requiredFields.length > 0) {
      prop.required = field.requiredFields;
    }
  }

  return prop;
};
